import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useGetShippingLabelQuery,
} from '../slices/ordersApiSlice';

import { BASE_URL } from '../constants';

const ShippingLabelScreen = () => {
  const { id: orderId } = useParams();
  const navigate = useNavigate();

  const [showPreview, setShowPreview] = useState(false);

  const {
    data: shippingLabel,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetShippingLabelQuery(orderId, {
    skip: !orderId,
  });

  const downloadUrl =
    `${BASE_URL}/api/orders/${orderId}/label/download`;

  const handleBack = () => {
    navigate(-1);
  };

  /*
   * ----------------------------------------------------------
   * Missing order ID
   * ----------------------------------------------------------
   */

  if (!orderId) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          Bestelling niet gevonden.
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleBack}
        >
          Terug
        </button>
      </div>
    );
  }

  /*
   * ----------------------------------------------------------
   * Loading
   * ----------------------------------------------------------
   */

  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div
          className="spinner-border text-primary mb-3"
          role="status"
          aria-label="Laden"
        />

        <h2>Verzendlabel</h2>

        <p className="text-muted">
          We halen de gegevens van het DHL-verzendlabel op...
        </p>
      </div>
    );
  }

  /*
   * ----------------------------------------------------------
   * API error
   * ----------------------------------------------------------
   */

  if (isError) {
    const errorMessage =
      error?.data?.message ||
      error?.error ||
      'Er is een onbekende fout opgetreden.';

    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">
            Verzendlabel niet beschikbaar
          </h4>

          <p>
            We konden de gegevens van het verzendlabel niet ophalen.
          </p>

          <p className="mb-0">
            <strong>Melding:</strong> {errorMessage}
          </p>
        </div>

        <button
          type="button"
          className="btn btn-primary me-2"
          onClick={() => refetch()}
        >
          Opnieuw proberen
        </button>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleBack}
        >
          Terug
        </button>
      </div>
    );
  }

  /*
   * ----------------------------------------------------------
   * DHL label creation failed or label is unavailable
   * ----------------------------------------------------------
   */

  const labelIsAvailable = shippingLabel?.status === 'success'
    || (Boolean(shippingLabel?.labelId) && shippingLabel?.status !== 'error');

  if (!labelIsAvailable) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">
            Verzendlabel niet beschikbaar
          </h4>

          <p>
            Het DHL-verzendlabel voor deze bestelling kon niet
            automatisch worden aangemaakt.
          </p>

          {shippingLabel?.errorMessage && (
            <p className="mb-0">
              <strong>DHL-melding:</strong>{' '}
              {shippingLabel.errorMessage}
            </p>
          )}
        </div>

        <p>
          Probeer het later opnieuw. Als het probleem blijft bestaan,
          neem dan contact op met de klantenservice.
        </p>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleBack}
        >
          Terug
        </button>
      </div>
    );
  }

  /*
   * ----------------------------------------------------------
   * Successful label
   * ----------------------------------------------------------
   */

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <h2 className="mb-0">
          DHL-verzendlabel
        </h2>

        <span className="badge bg-success fs-6">
          Beschikbaar
        </span>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            Verzendgegevens
          </h5>

          <p>
            <strong>Trackingnummer:</strong>{' '}
            {shippingLabel.trackingNumber || 'Niet beschikbaar'}
          </p>

          {shippingLabel.weightKg != null && (
            <p>
              <strong>Gewicht:</strong>{' '}
              {Number(shippingLabel.weightKg).toFixed(2)} kg
            </p>
          )}

          <p className="text-muted mb-0">
            Je kunt het verzendlabel hieronder bekijken of downloaden.
          </p>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowPreview(true)}
        >
          Verzendlabel bekijken
        </button>

        <a
          href={downloadUrl}
          className="btn btn-success"
          download={`DHL-verzendlabel-${orderId}.pdf`}
        >
          Verzendlabel downloaden
        </a>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleBack}
        >
          Terug
        </button>
      </div>

      {showPreview && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <strong>Voorbeeld verzendlabel</strong>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowPreview(false)}
            >
              Voorbeeld sluiten
            </button>
          </div>

          <div className="card-body p-0">
            <iframe
              src={downloadUrl}
              title="DHL-verzendlabel"
              width="100%"
              height="700"
              style={{
                border: 0,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingLabelScreen;